import type { JSX } from 'react';
import { FormSection, Radio } from '@ksyos/design-system';
import type { BariPostNurseSpecialistFollowUpData } from '@common/validation/customControls/bariPost/bariPostNurseSpecialistFollowUp/types';
import { ontlastingType } from '@common/customControls/bariatrics';
import {
    useProcessActivityContext,
    useProcessActivityEditContext,
} from '@frontend/utils/hooks/useProcessActivityContext';
import { BariatricsRokenEdit } from '../../../bariatrics/BariatricsRokenEdit';
import { BariPreSecretarialAssistentMedScreeningReportEditTabak } from '../../bariPre/bariPreSecretarialAssistentMedScreeningReport/v2/BariPreSecretarialAssistantMedScreeningReportEditTabak';
import {
    BooleanRadioGroupFormGroup,
    NumberInputColumnRowFormGroup,
    RadioGroupFormGroup,
    TextAreaFormGroup,
} from '../../processActivityFormGroupFields';

export function BariPostNurseSpecialistFollowUpLeefstijlEnMedicatieEdit(): JSX.Element {
    const { customControlVersion } = useProcessActivityContext();
    const { data, onChange } = useProcessActivityEditContext<BariPostNurseSpecialistFollowUpData>();

    const shouldShowRoken = customControlVersion === 1 && 'roken' in data;
    const shouldShowTabak = customControlVersion === 2 && 'tabak_gebruik' in data;

    return (
        <FormSection title="Leefstijl en medicatie">
            {/* Anamnese */}
            <TextAreaFormGroup
                fieldKey="beloop_aanvullende_anamnese"
                label="Anamnese (leefstijl, evt. klachten)"
            />
            {customControlVersion === 2 && (
                <TextAreaFormGroup
                    fieldKey="toelichting_deficienties_labuitslag"
                    label="Toelichting deficiënties op basis van labuitslag"
                />
            )}
            {/* Multivitaminen */}
            <BooleanRadioGroupFormGroup
                fieldKey="multivitaminen"
                isClearable={true}
                label="Gebruik multivitaminen"
                onChange={handleChangeMultivitaminen}
                type="radio"
            />
            {data.multivitaminen && (
                <TextAreaFormGroup
                    fieldKey="multivitaminen_toelichting"
                    isRequired={true}
                    label="Toelichting multivitaminegebruik"
                />
            )}
            {/* Alcohol */}
            <BooleanRadioGroupFormGroup
                fieldKey="alcohol"
                isClearable={true}
                label="Alcohol"
                onChange={handleChangeAlcohol}
                type="radio"
            />
            {data.alcohol && (
                <>
                    <NumberInputColumnRowFormGroup
                        appendedLabel="per week"
                        fieldKey="alcohol_aantal_eenheden_per_week"
                        label="Aantal eenheden"
                        maximumFractionDigits={2}
                        tooltipText="Elk glas staat gelijk aan 1 eenheid. Een standaardglas alcoholhoudende drank bevat rond de 10 gram alcohol (ca. 12 ml). Dit zit ongeveer in 250 ml bier, 100 ml wijn en 35 ml sterke drank."
                    />

                    <TextAreaFormGroup
                        fieldKey="alcohol_toelichting"
                        label="Toelichting alcohol"
                    />
                </>
            )}
            {shouldShowRoken && (
                <BariatricsRokenEdit
                    data={data}
                    onChange={onChange}
                />
            )}
            {shouldShowTabak && <BariPreSecretarialAssistentMedScreeningReportEditTabak />}
            {/* Medicatie */}
            <BooleanRadioGroupFormGroup
                fieldKey="medicatie"
                isClearable={true}
                label="Gebruik medicatie"
                onChange={handleChangeMedicatie}
                type="radio"
            />
            {data.medicatie && (
                <TextAreaFormGroup
                    fieldKey="huidige_medicatie"
                    label="Huidige medicatie"
                />
            )}
            {/* Ontlasting */}
            <RadioGroupFormGroup
                fieldKey="ontlasting"
                isClearable={true}
                label="Ontlasting"
            >
                {ontlastingType.map((value) => (
                    <Radio
                        key={value}
                        label={value}
                        value={value}
                    />
                ))}
            </RadioGroupFormGroup>
            <NumberInputColumnRowFormGroup
                appendedLabel="keer per dag"
                fieldKey="ontlasting_frequentie_per_dag"
                label="Frequentie ontlasting"
                maximumFractionDigits={2}
            />
        </FormSection>
    );

    async function handleChangeAlcohol(value: boolean | null): Promise<void> {
        return onChange(
            {
                alcohol: value,
                alcohol_aantal_eenheden_per_week: null,
                alcohol_toelichting: null,
            },
            'alcohol'
        );
    }

    async function handleChangeMultivitaminen(value: boolean | null): Promise<void> {
        return onChange(
            {
                multivitaminen: value,
                multivitaminen_toelichting: null,
            },
            'multivitaminen'
        );
    }

    async function handleChangeMedicatie(value: boolean | null): Promise<void> {
        return onChange(
            {
                huidige_medicatie: null,
                medicatie: value,
            },
            'medicatie'
        );
    }
}
